import React, { useEffect, useState } from 'react';
import { Pencil, Plus, User, X } from 'lucide-react';
import { PanelSection } from '../shared/property-rows';
import { ContactCompanyLinksSection } from '../clients/ContactCompanyLinksSection';
import { CompanyHierarchySection } from '../clients/CompanyHierarchySection';
import { ReferenteInlineForm, EMPTY_REFERENTE_DRAFT } from '../clients/ReferenteInlineForm';
import { splitContactName } from '../clients/create-drawer/formDefaults';
import { useApp } from '../AppProvider';

interface LeadPeopleSectionProps {
  selectedClient: any;
  /** Abilita aggiungi/modifica/rimuovi referenti (permesso update su lead/cliente). */
  canEdit?: boolean;
  /** Persistenza: updateClient + refetch, stessa firma di LeadClassificationRows. */
  onLeadUpdate?: (id: string, updates: any) => Promise<boolean>;
}

/** Nome/etichetta del draft ricomposto nella shape attesa da persistClientReferenti. */
const draftToContact = (draft: Record<string, any>) => ({
  ...draft,
  name:
    `${(draft.first_name || '').trim()} ${(draft.last_name || '').trim()}`.trim() ||
    draft.email || 'Contatto senza nome',
  newsletter_subscribed: draft.newsletter_subscribed || false,
});

/**
 * Toggle "Persone" del pannello dettagli: referenti (client_contacts legacy +
 * persone via client_relationships, lista merged), con editing inline quando
 * l'utente ha il permesso update, più persone/aziende collegate first-class e
 * gerarchia aziende. Il salvataggio passa da updateClient({contacts}) che fa
 * upsert + diff soft-unlink (vedi persistClientReferenti).
 */
export function LeadPeopleSection({ selectedClient, canEdit = false, onLeadUpdate }: LeadPeopleSectionProps) {
  const { toast } = useApp();
  const baseContacts: any[] = (selectedClient.contacts || []).filter((c: any) => c.is_active !== false);

  // Optimistic: il refetch post-update arriva con lag, la lista risponde subito
  const [pendingContacts, setPendingContacts] = useState<any[] | null>(null);
  const contactsKey = baseContacts.map((c) => c.id || c.email || c.name).join('|');
  useEffect(() => { setPendingContacts(null); }, [selectedClient.id, contactsKey]);
  const contacts = pendingContacts ?? baseContacts;

  // Editing consentito solo se la lista è stata davvero caricata dal client:
  // su contacts undefined il diff soft-unlink scollegherebbe referenti mai visti
  const editable = canEdit && !!onLeadUpdate && Array.isArray(selectedClient.contacts);

  // null = chiuso; -1 = nuovo; >=0 = modifica del contatto a quell'indice
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<string, any>>(EMPTY_REFERENTE_DRAFT);
  const [confirmRemoveIdx, setConfirmRemoveIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  // Selettore "collega persona": vive qui perche' il pulsante sta nella riga
  // delle azioni insieme ad "Aggiungi referente", non dentro la lista.
  const [picking, setPicking] = useState(false);
  // La lista canonica delle persone collegate si legge da sola all'apertura:
  // dopo un salvataggio va rifatta leggere, altrimenti la persona appena
  // inserita spariva dallo schermo pur essendo a posto nel database
  // (segnalato da Stefano il 09/09/2026).
  const [versionePersone, setVersionePersone] = useState(0);

  useEffect(() => { setEditingIdx(null); setConfirmRemoveIdx(null); }, [selectedClient.id]);

  const openNew = () => { setDraft({ ...EMPTY_REFERENTE_DRAFT }); setEditingIdx(-1); };
  const openEdit = (idx: number) => {
    setDraft({ ...EMPTY_REFERENTE_DRAFT, ...contacts[idx], ...splitContactName(contacts[idx]?.name) });
    setEditingIdx(idx);
  };
  const setField = (k: string) => (v: any) => setDraft((p) => ({ ...p, [k]: v }));

  const persist = async (next: any[]) => {
    if (!onLeadUpdate) return false;
    setSaving(true);
    setPendingContacts(next);
    const ok = await onLeadUpdate(selectedClient.id, { contacts: next });
    setSaving(false);
    if (!ok) {
      setPendingContacts(null);
      toast.error('Errore durante il salvataggio dei referenti');
    } else {
      setVersionePersone((v) => v + 1);
    }
    return ok;
  };

  const saveDraft = async () => {
    if (!draft.first_name?.trim() && !draft.last_name?.trim() && !draft.email?.trim()) {
      setEditingIdx(null);
      return;
    }
    const next = [...contacts];
    if (editingIdx === -1) next.push(draftToContact(draft));
    else if (editingIdx !== null) next[editingIdx] = draftToContact({ ...next[editingIdx], ...draft });
    const ok = await persist(next);
    if (ok) setEditingIdx(null);
  };

  const removeContact = async (idx: number) => {
    setConfirmRemoveIdx(null);
    if (editingIdx === idx) setEditingIdx(null);
    await persist(contacts.filter((_: any, i: number) => i !== idx));
  };

  // "Persone" ovunque nel pannello: prima la stessa cosa era "persona" in un
  // punto e "referente" in un altro. "Referente" resta solo come etichetta di
  // ruolo sulla singola riga.
  const summary = contacts.length > 0
    ? `${contacts.length} ${contacts.length === 1 ? 'persona' : 'persone'}`
    : undefined;

  // Su una scheda azienda la lista che conta e' quella delle persone collegate
  // (client_relationships): porta la scheda della persona, le etichette, il
  // principale e lo scollega. Quei referenti arrivano nella lista unita con
  // `person_id` valorizzato, quindi qui restano solo i contatti vecchi mai
  // collegati a una persona. Prima ogni referente compariva DUE volte, una per
  // lista, sotto due intestazioni "Persone" annidate (visto l'08/09/2026).
  const showLinks = !!selectedClient.id && !!selectedClient.entity_type;
  const isCompany = selectedClient.entity_type === 'company';
  // Su un'azienda il blocco elenca persone, su una persona elenca aziende: il
  // titolo segue il contenuto, prima diceva "Persone" anche sopra un elenco di
  // aziende. Le anagrafiche vecchie senza tipo restano sul vecchio elenco.
  const titolo = showLinks && !isCompany ? 'Aziende' : 'Persone';
  const mostraNuova = editable && (!showLinks || isCompany);
  const mostraCollega = canEdit && showLinks;
  const legacyRows: Array<{ contact: any; index: number }> = contacts
    .map((c: any, i: number) => ({ contact: c, index: i }))
    .filter(({ contact }) => !(showLinks && isCompany && contact.person_id));

  const personeSection = (
    // Aperta di default: con chi si parla e' la prima cosa che si cerca
    // aprendo una scheda, non deve costare un clic (richiesta di Luca).
    <PanelSection title={titolo} summary={summary} defaultOpen>
      {/* Lista canonica: persone di un'azienda, o aziende di una persona */}
      {showLinks && (
        <ContactCompanyLinksSection
          clientId={selectedClient.id}
          mode={isCompany ? 'company' : 'person'}
          personEmail={isCompany ? undefined : selectedClient.email}
          picking={picking}
          onPickingChange={setPicking}
          versione={versionePersone}
          hideTitle
        />
      )}

      {/* Referenti vecchi, non ancora collegati a una persona */}
      {legacyRows.length > 0 && (
        <div className="mb-2 space-y-1">
          {legacyRows.map(({ contact, index }) => (
            <div
              key={contact.id || index}
              className="group/ref rounded px-1.5 py-1.5 transition-colors hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03]"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1.5">
                  <User className="h-3 w-3 flex-shrink-0 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
                  <span className="truncate text-[13px] font-medium text-[#37352F] dark:text-white">{contact.name}</span>
                  {contact.role && (
                    <span className="truncate text-[11px] text-[#787774] dark:text-gray-400">· {contact.role}</span>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  {contact.is_primary && (
                    <span className="rounded bg-[#FBF3DB] px-1.5 py-0.5 text-[10px] text-[#CB912F] dark:bg-[#59563B] dark:text-[#FFDC49]">
                      Principale
                    </span>
                  )}
                  {contact.is_referente && (
                    <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                      Referente
                    </span>
                  )}
                  {contact.is_rappresentante_legale && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      Rapp. Legale
                    </span>
                  )}
                  {editable && confirmRemoveIdx !== index && (
                    <span className="ml-0.5 inline-flex items-center gap-0.5 opacity-0 transition-opacity group-hover/ref:opacity-100 [@media(hover:none)]:opacity-100">
                      <button
                        type="button"
                        title="Modifica persona"
                        onClick={() => openEdit(index)}
                        className="rounded p-0.5 text-[rgba(55,53,47,0.4)] hover:bg-[rgba(55,53,47,0.08)] hover:text-[#37352F] dark:text-gray-500 dark:hover:bg-white/[0.08] dark:hover:text-white"
                      >
                        <Pencil className="h-3 w-3" strokeWidth={1.5} />
                      </button>
                      <button
                        type="button"
                        title="Rimuovi persona"
                        onClick={() => setConfirmRemoveIdx(index)}
                        className="rounded p-0.5 text-[rgba(55,53,47,0.4)] hover:bg-[rgba(55,53,47,0.08)] hover:text-red-500 dark:text-gray-500 dark:hover:bg-white/[0.08]"
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </span>
                  )}
                  {editable && confirmRemoveIdx === index && (
                    <span className="ml-0.5 inline-flex items-center gap-1.5 whitespace-nowrap text-[11px]">
                      <span className="text-[#787774] dark:text-gray-400">Rimuovere?</span>
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        className="rounded bg-red-500 px-2 py-0.5 text-white hover:bg-red-600"
                      >
                        Rimuovi
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRemoveIdx(null)}
                        className="rounded bg-[rgba(55,53,47,0.08)] px-2 py-0.5 text-[#787774] hover:bg-[rgba(55,53,47,0.14)] dark:bg-gray-700 dark:text-gray-300"
                      >
                        Annulla
                      </button>
                    </span>
                  )}
                </div>
              </div>
              {(contact.email || contact.phone) && (
                <div className="ml-[18px] mt-0.5 space-y-0.5">
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="block truncate text-[12px] text-[#337EA9] hover:underline dark:text-[#529CCA]">
                      {contact.email}
                    </a>
                  )}
                  {contact.phone && (
                    <a href={`tel:${contact.phone}`} className="block text-[12px] text-[#787774] dark:text-gray-400">
                      {contact.phone}
                    </a>
                  )}
                </div>
              )}
              {contact.notes && (
                <p className="ml-[18px] mt-1 whitespace-pre-wrap text-[11px] text-[#787774] dark:text-gray-400">
                  {contact.notes}
                </p>
              )}
              {editable && editingIdx === index && (
                <ReferenteInlineForm
                  title="Modifica persona"
                  draft={draft}
                  onField={setField}
                  onCancel={() => setEditingIdx(null)}
                  onSave={saveDraft}
                  saveLabel="Salva"
                  saving={saving}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Nuovo referente / collega una persona gia' in rubrica */}
      {editable && editingIdx === -1 && (
        <ReferenteInlineForm
          title="Nuova persona"
          draft={draft}
          onField={setField}
          onCancel={() => setEditingIdx(null)}
          onSave={saveDraft}
          saveLabel="Aggiungi"
          saving={saving}
        />
      )}
      {editingIdx === null && (mostraNuova || mostraCollega) && (
        <div className="mb-1 flex flex-wrap items-center gap-1">
          {mostraNuova && (
            <button
              type="button"
              onClick={openNew}
              className="flex items-center gap-1.5 rounded px-1.5 py-1 text-[13px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
              Nuova persona
            </button>
          )}
          {mostraCollega && !picking && (
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="flex items-center gap-1.5 rounded px-1.5 py-1 text-[13px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
              {isCompany ? 'Collega persona' : 'Collega azienda'}
            </button>
          )}
        </div>
      )}

    </PanelSection>
  );

  return (
    <>
      {personeSection}
      {/* Gerarchia societaria: sezione a se', non e' un fatto sulle persone */}
      {showLinks && isCompany && (
        <PanelSection title="Gerarchia">
          <CompanyHierarchySection companyId={selectedClient.id} />
        </PanelSection>
      )}
    </>
  );
}
